// Inline SVG status marks — the check / cross / warning / undo glyphs that used to
// be rendered as Unicode text (✓ ✕ ⚠ ↩), which drew at inconsistent weights and
// baselines across platforms (UX audit; session_state micro-debt #3). Same loose,
// round-stroke language as NavIcons/onboarding icons, sized 1em so they inherit the
// surrounding text size, and `currentColor` so the container owns the tone.
//
// All are decorative by default (aria-hidden) — the surrounding text carries the
// meaning. Pass `title` only where the mark stands alone without a text label.

import type { SVGProps } from 'react';

function markProps(extra?: SVGProps<SVGSVGElement>): SVGProps<SVGSVGElement> {
  return {
    className: 'status-mark',
    width: '1em',
    height: '1em',
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2.4,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true,
    focusable: false,
    ...extra,
  };
}

function titled(title?: string) {
  return title ? { role: 'img' as const, 'aria-label': title, 'aria-hidden': undefined } : {};
}

/** A check mark — success / done / completed. */
export function CheckMark({ title }: { title?: string } = {}) {
  return (
    <svg {...markProps(titled(title))}>
      <path d="M5 12.5l4.2 4.3L19 7" />
    </svg>
  );
}

/** An X — failure / rejected. */
export function CrossMark({ title }: { title?: string } = {}) {
  return (
    <svg {...markProps(titled(title))}>
      <path d="M6.5 6.5l11 11M17.5 6.5l-11 11" />
    </svg>
  );
}

/** A triangle-bang — a warning / divergence. */
export function WarningMark({ title }: { title?: string } = {}) {
  return (
    <svg {...markProps(titled(title))}>
      <path d="M12 4.5L21 19.5H3z" />
      <path d="M12 10.5v4" />
      <path d="M12 17.4v.1" strokeWidth={2.8} />
    </svg>
  );
}

/** A curved back-arrow — an undone action. */
export function UndoMark({ title }: { title?: string } = {}) {
  return (
    <svg {...markProps(titled(title))}>
      <path d="M9 7L4.5 11.5 9 16" />
      <path d="M4.5 11.5H14a5 5 0 0 1 5 5v0" />
    </svg>
  );
}
